import { CommonRegistry, EquipSlot } from '../components/components';
import { BodyTypeSlots, HAND_SLOTS } from '../data/BodyTypeSlots';
import { World } from '../World';
import { EventBusService } from './EventBusService';
import { InventoryOperations } from './InventoryService';

/**
 * Service for managing entity equipment.
 * Handles equip/unequip with slot validation based on body type.
 *
 * Hand slots (mainHand, offHand) accept any item.
 * Other slots require the item to have Equippable component with matching validSlots.
 */
export class EquipmentService {
  private readonly world: World<CommonRegistry>;
  private readonly inventoryOps: InventoryOperations;
  private readonly eventBus: EventBusService;

  constructor(
    world: World<CommonRegistry>,
    inventoryOps: InventoryOperations,
    eventBus: EventBusService,
  ) {
    this.world = world;
    this.inventoryOps = inventoryOps;
    this.eventBus = eventBus;
  }

  /**
   * Get valid slots for an entity based on its BodyType.
   */
  getValidSlots(entityId: number): EquipSlot[] {
    const bodyType = this.world.getComponent(entityId, 'BodyType');
    const bodyTypeName = bodyType?.bodyType ?? 'humanoid';
    return BodyTypeSlots[bodyTypeName] ?? [];
  }

  /**
   * Check if a slot is a hand slot (accepts any item).
   */
  isHandSlot(slot: EquipSlot): boolean {
    return HAND_SLOTS.includes(slot);
  }

  /**
   * Equip an item to a specific slot.
   * Removes item from inventory/current equipment and places in equipment slot.
   * If slot is occupied, swaps with existing item (returns to inventory).
   *
   * Hand slots (mainHand, offHand) accept any item.
   * Other slots require the item to have Equippable component with matching validSlots.
   *
   * @returns true if equip succeeded
   */
  equip(entityId: number, slot: EquipSlot, itemEntityId: number): boolean {
    // Validate the equip operation is allowed
    if (!this.canEquipToSlot(entityId, slot, itemEntityId)) {
      return false;
    }

    const equipment = this.world.getComponent(entityId, 'Equipment');
    if (!equipment) {
      return false;
    }

    // Find where item currently is
    const itemSource = this.getItemSource(entityId, itemEntityId);
    if (!itemSource) {
      return false; // Item not in entity's inventory or equipment
    }

    // If destination slot is occupied, try to swap
    const existingItemId = equipment.slots[slot] ?? null;
    if (existingItemId !== null) {
      if (!this.canSwapItem(entityId, existingItemId, itemSource)) {
        return false;
      }
    }

    // Execute the equip: remove from source, put in slot
    this.removeItemFromSource(entityId, itemSource);
    equipment.slots[slot] = itemEntityId;
    this.world.updateComponent(entityId, equipment, { slots: equipment.slots });

    // If swapping, move old item to inventory
    if (existingItemId !== null) {
      this.inventoryOps.addItem(entityId, existingItemId);
    }

    return true;
  }

  /**
   * Swaps items between two equipment slots for an entity.
   * @returns true if swap succeeded, false otherwise.
   */
  swap(entityId: number, slotA: EquipSlot, slotB: EquipSlot): boolean {
    if (slotA === slotB) {
      return true; // Swapping an item with itself is a no-op, consider it successful.
    }

    const equipment = this.world.getComponent(entityId, 'Equipment');
    if (!equipment) {
      return false;
    }

    const itemA = equipment.slots[slotA] ?? null;
    const itemB = equipment.slots[slotB] ?? null;

    // Case 1: Both slots are empty. Nothing to do.
    if (itemA === null && itemB === null) {
      return true;
    }

    // Case 2: slotA has itemA, slotB is empty.
    // Try to move itemA to slotB.
    if (itemA !== null && itemB === null) {
      if (!this.canEquipToSlot(entityId, slotB, itemA)) {
        return false; // itemA cannot be equipped in slotB
      }
      equipment.slots[slotB] = itemA;
      equipment.slots[slotA] = null;
      this.world.updateComponent(entityId, equipment, { slots: equipment.slots });
      return true;
    }

    // Case 3: slotA is empty, slotB has itemB.
    // Try to move itemB to slotA.
    if (itemA === null && itemB !== null) {
      if (!this.canEquipToSlot(entityId, slotA, itemB)) {
        return false; // itemB cannot be equipped in slotA
      }
      equipment.slots[slotA] = itemB;
      equipment.slots[slotB] = null;
      this.world.updateComponent(entityId, equipment, { slots: equipment.slots });
      return true;
    }

    // Case 4: Both slots have items (itemA and itemB).
    // Check if itemA can go into slotB and itemB can go into slotA.
    if (itemA !== null && itemB !== null) {
      if (!this.canEquipToSlot(entityId, slotB, itemA)) {
        return false; // itemA cannot be equipped in slotB
      }
      if (!this.canEquipToSlot(entityId, slotA, itemB)) {
        return false; // itemB cannot be equipped in slotA
      }

      // Perform the swap
      equipment.slots[slotA] = itemB;
      equipment.slots[slotB] = itemA;
      this.world.updateComponent(entityId, equipment, { slots: equipment.slots });
      return true;
    }

    return false; // Should not reach here
  }

  /**
   * Check if an item can be equipped to a specific slot.
   */
  public canEquipToSlot(entityId: number, slot: EquipSlot, itemEntityId: number): boolean {
    // Check slot is valid for entity's body type
    const validSlots = this.getValidSlots(entityId);
    if (!validSlots.includes(slot)) {
      return false;
    }

    // Hand slots accept any item; other slots require Equippable component
    if (!this.isHandSlot(slot)) {
      const equippable = this.world.getComponent(itemEntityId, 'Equippable');
      if (!equippable || !equippable.validSlots.includes(slot)) {
        return false;
      }
    }

    return true;
  }

  /**
   * Get where an item currently is (inventory slot or equipment slot).
   * Returns null if item is not owned by this entity.
   */
  private getItemSource(
    entityId: number,
    itemEntityId: number,
  ): { type: 'inventory'; slotIndex: number } | { type: 'equipment'; slot: EquipSlot } | null {
    // Check inventory first
    const itemLoc = this.inventoryOps.findItemLocation(itemEntityId);
    if (itemLoc) {
      if (itemLoc.inventoryEntityId !== entityId) {
        return null; // In someone else's inventory
      }
      return { type: 'inventory', slotIndex: itemLoc.slotIndex };
    }

    // Check equipment
    const equippedLoc = this.findEquippedItem(itemEntityId);
    if (equippedLoc) {
      if (equippedLoc.entityId !== entityId) {
        return null; // Equipped by someone else
      }
      return { type: 'equipment', slot: equippedLoc.slot };
    }

    return null;
  }

  /**
   * Check if a swap operation can be completed (existing item can fit in inventory).
   */
  private canSwapItem(
    entityId: number,
    existingItemId: number,
    itemSource: { type: 'inventory'; slotIndex: number } | { type: 'equipment'; slot: EquipSlot },
  ): boolean {
    // If source is inventory, the slot will be freed, so swap always works
    if (itemSource.type === 'inventory') {
      return true;
    }

    // If source is equipment, we need an empty inventory slot for the existing item
    // (or can use the inventory slot that will be freed if swapping between equipment)
    const existingItemLoc = this.inventoryOps.findItemLocation(existingItemId);
    if (existingItemLoc === null) {
      // existingItem isn't in inventory, check if there's space
      const inventory = this.world.getComponent(entityId, 'Inventory');
      if (!inventory) return false;
      const hasEmptySlot = inventory.slots.some((s) => s === null);
      if (!hasEmptySlot) return false;
    }
    return true;
  }

  /**
   * Remove an item from its source location.
   */
  private removeItemFromSource(
    entityId: number,
    source: { type: 'inventory'; slotIndex: number } | { type: 'equipment'; slot: EquipSlot },
  ): void {
    if (source.type === 'inventory') {
      this.inventoryOps.removeItem(entityId, source.slotIndex);
    } else {
      const equipment = this.world.getComponent(entityId, 'Equipment');
      if (equipment) {
        equipment.slots[source.slot] = null;
        this.world.updateComponent(entityId, equipment, { slots: equipment.slots });
      }
    }
  }

  /**
   * Unequip an item from a slot, returning it to inventory.
   * @returns The item entity ID, or null if slot was empty or inventory full
   */
  unequip(entityId: number, slot: EquipSlot, targetSlot?: number): number | null {
    const equipment = this.world.getComponent(entityId, 'Equipment');
    if (!equipment) return null;

    const itemEntityId = equipment.slots[slot];
    if (itemEntityId === undefined || itemEntityId === null) {
      return null;
    }

    // Try to add to inventory
    const addResult = this.inventoryOps.addItem(entityId, itemEntityId, targetSlot);
    if (addResult === null) {
      return null; // Inventory full or target slot invalid
    }

    equipment.slots[slot] = null;
    this.world.updateComponent(entityId, equipment, { slots: equipment.slots });
    return itemEntityId;
  }

  /**
   * Attempt to drop a specific item from an equipment slot.
   * Emits an ItemDropped event if successful.
   * @returns The item entity ID if drop was triggered, null otherwise.
   */
  dropItem(entityId: number, slot: EquipSlot): number | null {
    const itemEntityId = this.getEquipped(entityId, slot);
    if (itemEntityId === null) {
      return null;
    }

    // Emit drop event
    this.eventBus.addEvent({
      name: 'ItemDropped',
      source: 'EquipmentService',
      payload: {
        dropperEntityId: entityId,
        itemEntityId: itemEntityId,
        slotName: slot,
      },
    });

    return itemEntityId;
  }

  /**
   * Get the item equipped in a specific slot.
   */
  getEquipped(entityId: number, slot: EquipSlot): number | null {
    const equipment = this.world.getComponent(entityId, 'Equipment');
    if (!equipment) return null;
    return equipment.slots[slot] ?? null;
  }

  /**
   * Get all equipped items for an entity.
   * @returns Map of slot to item entity ID (only populated slots)
   */
  getAllEquipped(entityId: number): Partial<Record<EquipSlot, number>> {
    const equipment = this.world.getComponent(entityId, 'Equipment');
    if (!equipment) return {};

    const result: Partial<Record<EquipSlot, number>> = {};
    for (const [slot, itemId] of Object.entries(equipment.slots)) {
      if (itemId !== null && itemId !== undefined) {
        result[slot as EquipSlot] = itemId;
      }
    }
    return result;
  }

  /**
   * Find which entity and slot an item is equipped in.
   */
  findEquippedItem(itemEntityId: number): { entityId: number; slot: EquipSlot } | null {
    // Query all entities with Equipment component
    const equippedEntities = this.world.query(['Equipment']);

    for (const entityId of equippedEntities) {
      const equipment = this.world.getComponent(entityId, 'Equipment');
      if (!equipment) continue;

      for (const [slotName, equippedId] of Object.entries(equipment.slots)) {
        if (equippedId === itemEntityId) {
          return { entityId, slot: slotName as EquipSlot };
        }
      }
    }
    return null;
  }
}
